import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useCreateCheckoutSession } from "~/api/billing/billing";
import { AppLogo } from "~/components/AppLogo";
import { Button } from "~/components/ui/button";
import { ROUTES } from "~/helpers";
import { PricingCard } from "./components/PricingCard";
import { TestimonialTicker } from "./components/TestimonialTicker";
import { PRICING_TIERS, SHARED_FEATURES, TESTIMONIALS } from "./constants";

interface UsageLimitModalProps {
  organizationId: string;
  freeTrialLimit?: number;
  isLimitReached?: boolean;
  onClose?: () => void;
}

export const UsageLimitModal: React.FC<UsageLimitModalProps> = ({
  organizationId,
  freeTrialLimit = 1000,
  isLimitReached = true,
  onClose,
}) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { mutate: createCheckoutSession, isPending } =
    useCreateCheckoutSession();

  const handleSelectPlan = (planId: string) => {
    const plan = PRICING_TIERS.find((tier) => tier.id === planId);
    if (!plan) {
      return;
    }
    createCheckoutSession(
      { organizationId, data: { organizationPlan: plan.plan } },
      {
        onSuccess: (data) => {
          if (data.checkoutUrl) {
            window.open(data.checkoutUrl, "_blank");
          }
        },
      }
    );
  };

  const handleBackToCases = () => {
    navigate(ROUTES.CASES({ organizationId }));
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-background"
      role="dialog"
      aria-modal="true"
      aria-labelledby="usage-limit-title"
    >
      <div className="flex min-h-full flex-col items-center justify-center px-4 py-12">
        {/* Header */}
        <div className="mx-auto mb-8 max-w-xl text-center">
          <div className="mb-6 flex items-center justify-center">
            <AppLogo />
          </div>
          <h1
            id="usage-limit-title"
            className="text-2xl font-semibold tracking-tight text-foreground"
          >
            {isLimitReached
              ? formatMessage(
                  {
                    id: "billing.upgrade.title",
                    defaultMessage: "You\u2019ve processed {limit} pages",
                  },
                  { limit: freeTrialLimit.toLocaleString() }
                )
              : formatMessage({
                  id: "billing.upgrade.voluntaryTitle",
                  defaultMessage: "Choose Your Plan",
                })}
          </h1>
          <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
            {isLimitReached ? (
              <FormattedMessage
                id="billing.upgrade.subtitle"
                defaultMessage="Your documents and settings are saved. Pick a plan to continue."
              />
            ) : (
              <FormattedMessage
                id="billing.upgrade.voluntarySubtitle"
                defaultMessage="Pick a plan that fits your workflow."
              />
            )}
          </p>
        </div>

        {/* Plan Cards */}
        <div className="mb-6 grid w-full max-w-3xl grid-cols-1 gap-5 md:grid-cols-3">
          {PRICING_TIERS.map((tier) => (
            <PricingCard
              key={tier.id}
              tier={tier}
              onSelect={handleSelectPlan}
              isPending={isPending}
            />
          ))}
        </div>

        {/* Shared features */}
        <div className="mb-6 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
          {SHARED_FEATURES.map((feature, i) => (
            <React.Fragment key={feature.id}>
              {i > 0 && <span>&middot;</span>}
              <span>
                {formatMessage({
                  id: feature.id,
                  defaultMessage: feature.defaultMessage,
                })}
              </span>
            </React.Fragment>
          ))}
        </div>

        {/* Testimonials */}
        <TestimonialTicker testimonials={TESTIMONIALS} />

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            <FormattedMessage
              id="billing.upgrade.pricesInUsd"
              defaultMessage="All prices in USD. You\u2019ll enter payment details on the next step."
            />
          </p>
          <Button
            variant="link"
            className="mt-3 text-xs text-blue-600 underline underline-offset-2 hover:text-blue-700"
            onClick={handleBackToCases}
          >
            <FormattedMessage
              id="billing.upgrade.backToCases"
              defaultMessage="Back to the list of cases"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
